/** Apply a server display-list DELTA in place of refetching the whole thing.
 *
 * ⚠⚠⚠ **THIS IS A TRANSCRIPTION OF `display.apply_delta`, NOT A SECOND IMPLEMENTATION.**
 * The Python reference is the one `qa/patch_check.py` verifies against a full rebuild — 54 of 54
 * cases, six operations, three drawings, forward and undone. If these two ever disagree the
 * browser is wrong, because only one of them is gated. Keep them line-for-line comparable.
 *
 * ⚠ Why a delta at all: editing drawing 1158 (62,210 entities) refetched a **46 MB** display list
 * after every MOVE. The same edit's delta is **88.6 KB** — 520x smaller — because `display.patch`
 * already knows exactly which rows changed and was throwing that away.
 */

/** What the server sends. ⚠ `splice` is in the OLD numbering — see `applyDelta`. */
export interface DisplayDelta {
  splice?: Record<string, [number, number, unknown[]][]>;
  /** ⚠ `[polyIndex, textIndex]` for EVERY fill, not just the changed ones — see below. */
  fills_at?: [number, number][];
  widened?: number[];
  uframes?: number[];
  extents?: number[];
  counts?: Record<string, number>;
  ltypes?: number[][];
  tstyles?: unknown[][];
  /** ⚠ A block reference's attribute VALUES — `Panels.tsx` reads these. */
  atts?: Record<string, [string, string][]>;
}

/** The channels a delta can splice. ⚠ Same list as `display.CHANNELS`, same order.
 *
 * ⚠⚠ **`images` AND `underlays` ARE ON THE END AND ARE NOT `display.CHANNELS`.** They are not
 * row channels — they carry no slice index and a patch never rewrites one — but a DELETE that
 * takes every image and underlay with it must be able to EMPTY them, and that is an ordinary
 * `[0, n, []]`. Three raster images out of 11,504 objects were refusing the whole patch of an
 * ERASE ALL on `ysl-hn-center-2025`, which cost the browser a 22 MB refetch. See `drop_media` in
 * `rust/pdcad-core/src/splice.rs`; a delta that does not mention them is unaffected. */
const CHANNELS = ["polys", "texts", "fills", "marks", "cen", "ins",
                  "images", "underlays"] as const;

/** A display list with the delta applied. ⚠ Does NOT mutate `dl`.
 *
 * ⚠ Generic over the caller's own type rather than `Record<string, unknown>`: `DisplayList` is an
 * interface with named fields and no index signature, so the constrained form does not accept it
 * and would force a cast at every call site instead of one here.
 */
/** For each channel array this produced, the index before which every row is the SAME OBJECT as
 *  in the array it came from.
 *
 * ⚠⚠⚠ **`gpu/build.ts` WAS PROVING THIS WITH A LOOP OVER 1.5 MILLION ROWS, ON EVERY EDIT.** Its
 * band-box cache is only reusable while the old polys are untouched, and the test it had was an
 * element-wise identity scan of the whole previous array — so the cache cost ~12-25 ms of the
 * ~150 ms an edit takes, to avoid a fold that costs about the same. `applyDelta` KNOWS: a splice
 * at index `at` with `remove` rows leaves everything below `at` exactly where it was, and a DRAW
 * appends at the end.
 * ⚠ A `WeakMap` and not a field on the array: the display list is compared by identity in
 * several places and handed to `JSON.stringify` in the local engine, and a stray property would
 * eventually be one of those. The entry dies with the array.
 * ⚠ `undefined` means "nothing is known", which is what every other producer of a channel array
 * leaves behind — a full build, a refetch, the local engine. The caller must treat it as 0. */
const UNCHANGED_BEFORE = new WeakMap<object, number>();

export function unchangedBefore(arr: unknown): number | undefined {
  return arr && typeof arr === "object"
    ? UNCHANGED_BEFORE.get(arr as object) : undefined;
}

/** The other end of the same fact: the index at or after which every row is the same object AND
 *  still at the same index.
 *
 * ⚠⚠⚠ **`unchangedBefore` ALONE LEFT THE COMMONEST EDIT THERE IS PAYING A FULL FOLD.** The band
 * cache in `gpu/build.ts` could inherit its boxes across an APPEND (a draw) and a TRUNCATION (an
 * undo) and had no case for a REPLACEMENT — which is what MOVE, ROTATE, SCALE, MIRROR, STRETCH,
 * TRIM, EXTEND, FILLET and MATCHPROP all are. So every one of them re-folded 1.5 million boxes
 * to account for the one row that moved: ~25 ms of a ~52 ms MOVE on `ysl-hn-center-2025`.
 * ⚠ Set ONLY when every cut put back exactly as many rows as it removed. That is what makes the
 * indices above it meaningful — a splice that changes the length shifts everything after it, and
 * an inherited box would then belong to a different row.
 * ⚠ `undefined` means "not a pure replacement, or nothing is known"; the caller must treat that
 * as "no inheritance", the same as `unchangedBefore`. */
const SAME_FROM = new WeakMap<object, number>();

export function sameFrom(arr: unknown): number | undefined {
  return arr && typeof arr === "object" ? SAME_FROM.get(arr as object) : undefined;
}

/** The cuts that produced this array, in the OLD numbering — `[at, remove, rows]`, as sent.
 *
 * ⚠⚠⚠ **SO A CONSUMER CAN UPDATE A DERIVED FACT INSTEAD OF RECOMPUTING IT.** `Studio.selKinds`
 * walks every row of every channel to collect the dozen strings the ribbon's kind picker offers
 * — 16.7 ms of the 52.8 ms render an edit costs on `ysl-hn-center-2025`, to learn that a drawing
 * which had LINEs before still has LINEs. With the cuts in hand the same answer is exact and
 * costs only the rows that actually changed.
 * ⚠ The OLD numbering is the point: paired with the array this one was spliced FROM, each cut
 * says which rows left and which arrived, which is what a count needs.
 * ⚠ `undefined` from every other producer — a full build, a refetch, the local engine — and the
 * caller must fall back to a full pass, exactly as with `unchangedBefore`. */
const CUTS = new WeakMap<object, readonly [number, number, unknown[]][]>();

export function cutsOf(arr: unknown): readonly [number, number, unknown[]][] | undefined {
  return arr && typeof arr === "object" ? CUTS.get(arr as object) : undefined;
}

export function applyDelta<T>(dl: T, d: DisplayDelta): T {
  const src = dl as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = { ...src };

  // ⚠⚠ **LAST TO FIRST.** Every `at` is an index into the OLD array, so applying them forwards
  // invalidates every range after the first one. Sorting descending means no index is disturbed
  // before it is used, which is why the server can send them in plain ascending order and this
  // stays a three-line loop.
  for (const ch of CHANNELS) {
    const cuts = d.splice?.[ch];
    if (!cuts) continue;
    const arr = (src[ch] as unknown[] | undefined)?.slice() ?? [];
    // ⚠ The lowest index any splice touches — see `UNCHANGED_BEFORE`. Taken BEFORE they are
    // applied, in the OLD numbering, which is the numbering `at` is in.
    let low = Infinity;
    // ⚠ The other end, for `sameFrom`: the highest index any cut wrote, and whether every cut
    // was a pure replacement. `remove !== rows.length` anywhere means the indices above it moved.
    let high = -Infinity;
    let pure = true;
    for (const [at, remove, rows] of cuts) {
      if (at < low) low = at;
      if (remove !== rows.length) pure = false;
      if (at + rows.length > high) high = at + rows.length;
    }
    for (const [at, remove, rows] of [...cuts].sort((a, b) => b[0] - a[0])) {
      arr.splice(at, remove, ...rows);
    }
    // ⚠⚠ **RELATIVE TO THE ARRAY THIS ONE WAS SPLICED FROM, AND NOT CHAINED.** The first
    // version took the minimum with the SOURCE array's own mark, reasoning that an array spliced
    // twice is only untouched below the lower of the two. That is the right answer to a
    // different question — "untouched since when?" — and it made the mark fall behind by one
    // edit for ever: after two appends it read the length before the first, the band cache's
    // `polys.length` was the length after it, and the test said no on every edit from then on.
    // The one caller compares against the IMMEDIATELY previous array (`prev.bandCache.polys ===
    // prevDl.polys`), which is exactly what `low` is measured against.
    UNCHANGED_BEFORE.set(arr, low);
    if (pure && high >= 0) SAME_FROM.set(arr, high);
    CUTS.set(arr, cuts as readonly [number, number, unknown[]][]);
    out[ch] = arr;
  }

  // ⚠⚠⚠ **THE INDEX COLUMNS ARE REPLACED WHOLESALE, AND ONLY AFTER THE SPLICES.** `fills[6]` is
  // a poly index and `fills[7]` a texts index — both are COUNTS taken when the fill was emitted,
  // so every unchanged fill after a splice point is stale by however far that splice moved. The
  // server sends the two integers for every fill rather than a shift rule: the arithmetic behind
  // that rule was got wrong five separate ways on the server, and it is not going to be written
  // twice.
  // ⚠ COPY the rows before writing. A channel with no splice is still the caller's own array,
  // and editing it in place rewrites the display list React is holding — the Python reference
  // had exactly this bug and it silently corrupted the list every test compared against.
  if (d.fills_at) {
    // ⚠⚠⚠ **ONLY THE ROWS THAT ACTUALLY MOVED.** This copied EVERY fill row to write two
    // integers, and most of them were already right: a splice near the end of `polys` shifts no
    // index before it, and a DRAW appends, so nothing shifts at all. Two costs, not one —
    // `gpu/build.ts` caches a fill's triangles in a `WeakMap` keyed on the point array's
    // IDENTITY, so handing back fresh rows threw that cache away on every edit. The same
    // measurement `applyLocalTransform` records: 316 ms of it.
    // ⚠ The rows that DO move are copied, never written in place — the array is still the one
    // React is holding, and the Python reference had exactly that bug.
    const fills = (out.fills as unknown[][] | undefined) ?? [];
    let next: unknown[][] | null = null;
    for (let i = 0; i < d.fills_at.length && i < fills.length; i++) {
      const [a, b] = d.fills_at[i];
      if (fills[i][6] === a && fills[i][7] === b) continue;
      if (!next) next = fills.slice();
      const row = fills[i].slice();
      row[6] = a;
      row[7] = b;
      next[i] = row;
    }
    if (next) out.fills = next;
  }

  // ⚠ `widened` and `uframes` are arrays of ROW INDICES, not rows — small enough to send whole,
  // and remapping them client-side would be the same arithmetic again.
  // ⚠ `atts` is in this list because the panel reads it: a drafter who edited a block
  // reference kept seeing its OLD attribute text until a reload, because `patch` recomputed
  // `atts` for the FILE and the delta never carried it.
  for (const k of ["widened", "uframes", "extents", "counts", "ltypes", "tstyles",
                   "atts"] as const) {
    if (d[k] != null) out[k] = d[k];
  }
  return out as unknown as T;
}

/** The delta for the space on screen, or `undefined` when the server could not produce one.
 *
 * ⚠ A missing delta is NORMAL and means "refetch": `display.patch` declines whenever it cannot
 * be certain — a block definition was edited, an image moved, the space has composed viewports.
 * Treating absence as an error would turn a deliberate, safe refusal into a broken screen.
 */
/** Did the server say the picture is UNCHANGED by this edit? Then there is nothing to fetch.
 *
 * ⚠⚠ F3 used to cost the next refetch 17 s on drawing 1158: toggling a snap writes `OSMODE`
 * through SETVAR, SETVAR is an edit, and an edit invalidated the whole display cache. The
 * server now keeps the picture for a command that cannot change it (`edits.picture_unchanged`)
 * and says so in the report; `refresh` reads this and leaves the list on screen alone. */
export function keptBy(rep: unknown): boolean {
  return !!(rep as { _redraw?: { kept?: boolean } })?._redraw?.kept;
}

export function deltaFor(rep: unknown, space: string | null | undefined): DisplayDelta | undefined {
  const d = (rep as { _redraw?: { _deltas?: Record<string, DisplayDelta> } })?._redraw?._deltas;
  return d?.[space || "Model"];
}

/** `[a, b, c, d, e, f]` — the same six numbers `Canvas2D`'s preview uses. */
export type Affine = [number, number, number, number, number, number];

/** ⚠⚠ **THE ROW LAYOUTS THIS TRANSFORMS, AND THE TWO CHANNELS IT CANNOT.** From
 *  `display.py`'s own schema:
 *      polys `[layer, aci, flatPts, owner, kind, …]`      texts `[layer, aci, x, y, …, owner, …]`
 *      fills `[layer, aci, flatPts, owner, kind, …]`      marks `[layer, aci, x, y, owner, kind]`
 *      rays  `[layer, aci, x, y, dx, dy, half, owner, kind]`
 *  ⚠⚠⚠ **`cen` AND `ins` CARRY NO OWNER** — they are flat `[layer, x, y, …]` on purpose, because
 *  a drawing full of light fittings has thousands of circle centres — and `display.py` says so
 *  where it declares them: *"NOTHING in them says whose a point is"*. Only the server's sidecar
 *  knows, so they are LEFT ALONE here and the caller must stand its CENTER and INSERTION snaps
 *  down until the real delta lands. Moving the picture and leaving those points behind is the
 *  defect that file warns about: *"the drafter would snap to where it used to be, which no count
 *  and no picture can see."*
 */
/** Publish a timing for a harness. ⚠⚠ **GUARDED, BECAUSE THESE MODULES RUN IN NODE.**
 *  `qa/optimistic_check.mjs` and `qa/scene_check.mjs` import this file directly and there is no
 *  `window` there — an unguarded write took the gate down with `ReferenceError: window is not
 *  defined`, which reads like a broken gate rather than a broken probe. */
const note = (k: string, ms: number) => {
  if (typeof window === "undefined") return;
  (window as unknown as Record<string, unknown>)[k] = Math.round(ms * 10) / 10;
};

const OWNER_AT: Record<string, number> = { polys: 3, texts: 7, fills: 3, marks: 4, rays: 7 };
//: Where each of those rows carries its owner's KIND. ⚠ Same channels, same order, one table
//: each — two parallel literals inside one function is how they drift.
const KIND_AT: Record<string, number> = { polys: 4, texts: 8, fills: 4, marks: 5, rays: 8 };

/** The rows a local transform would touch, and whether it may — ONE pass, not two.
 *
 * ⚠⚠⚠ **`canTransformLocally` AND `applyLocalTransform` WERE SCANNING THE SAME 1.5 MILLION ROWS
 * FOR THE SAME OWNERS, ONE AFTER THE OTHER, INSIDE ONE REACT RENDER.** Measured on
 * `ysl-hn-center-2025`: 12.8 ms to decide it was allowed and 13.3 ms to do it, of a 53.9 ms
 * optimistic render. The first pass already knows exactly which rows the second one wants.
 * ⚠ `null` means "do not draw this before the server answers" — an unknown kind, a kind not in
 * `LOCALLY_EXACT_BY_OP` for this op, or no handles.
 * ⚠ A handle the list does not draw at all is refused: absence is not evidence of a safe kind.
 */
export interface LocalPlan { rows: Record<string, number[]> }

export function planLocal<T>(dl: T, handles: string[], op = "move"): LocalPlan | null {
  const _p0 = performance.now();
  const allowed = LOCALLY_EXACT_BY_OP[op];
  if (!allowed || !handles.length) return null;
  const src = dl as unknown as Record<string, unknown>;
  const want = new Set(handles.map((h) => h.toUpperCase()));
  const kindOf = new Map<string, string>();
  const rows: Record<string, number[]> = {};
  for (const [ch, oi] of Object.entries(OWNER_AT)) {
    const arr = src[ch] as unknown[][] | undefined;
    if (!arr || !arr.length) continue;
    const ki = KIND_AT[ch];
    const hit: number[] = [];
    for (let i = 0; i < arr.length; i++) {
      const o = arr[i][oi] as string;
      if (!o || !want.has(o)) continue;
      hit.push(i);
      // ⚠⚠ **THE KIND IS READ OFF THE DISPLAY LIST, NOT OFF THE SELECTION.** `Studio`'s `sel`
      // carries a `kind` and in at least one path it is a PLACEHOLDER — the result of a JOIN is
      // pushed back as `"LWPOLYLINE"` whatever it is. Guessing a block reference's geometry
      // because a stale label said LWPOLYLINE would draw the wrong picture.
      if (!kindOf.has(o)) kindOf.set(o, arr[i][ki] as string);
    }
    if (hit.length) rows[ch] = hit;
  }
  note("__pdcadPlanMs", performance.now() - _p0);
  for (const h of handles) {
    const k = kindOf.get(h.toUpperCase());
    if (!k || !allowed.has(k)) return null;
  }
  return { rows };
}

/** A display list with `m` applied to every row owned by `owners`. ⚠ Does NOT mutate `dl`.
 *
 * ⚠⚠⚠ **WHY THIS IS SAFE TO SPLICE A SERVER DELTA OVER AFTERWARDS.** The delta's `[at, remove,
 * rows]` are indices into the list as the SERVER last knew it, and this changes no row's
 * position and no channel's length — only coordinates inside rows it copies. So the authoritative
 * delta lands exactly as it would have, and replaces these rows outright. ⚠ A transform that
 * ADDED or REMOVED a row (COPY, ERASE) would invalidate every index after it, which is why the
 * caller admits translations only.
 */
export function applyLocalTransform<T>(dl: T, owners: Set<string>, m: Affine,
                                       plan?: LocalPlan | null): T {
  //: ⚠ Published because this runs inside React's RENDER (`Studio.commit` calls it from the
  //: `setDl` updater), so it is not a cost anything else can attribute. Read by a harness only.
  const _t0 = performance.now();
  const src = dl as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = { ...src };
  const [a, b, c, d, e, f] = m;
  const fx = (x: number, y: number) => a * x + c * y + e;
  const fy = (x: number, y: number) => b * x + d * y + f;

  for (const [ch, oi] of Object.entries(OWNER_AT)) {
    const arr = src[ch] as unknown[][] | undefined;
    if (!arr || !arr.length) continue;
    let touched = false;
    // ⚠⚠⚠ **`slice()` AND A LOOP, NOT `map()` — THIS RUNS INSIDE REACT'S RENDER.** `Studio.commit`
    // calls it from the `setDl` updater, so its cost is the optimistic render: 56.5 ms of one
    // MOVE on `ysl-hn-center-2025`, against 7.8 ms for the render that lands the server's real
    // delta. `map` invokes a closure per row and allocates as it goes; `slice` is a memcpy of
    // 1.5 million pointers and the loop writes only the rows that moved. Same output, same
    // identity for every untouched row — which `gpu/build.ts` depends on for its fill cache.
    const next = arr.slice();
    // ⚠ With a plan there is no search: `planLocal` already walked these rows to decide the
    // transform was allowed at all, and threw the answer away. Without one — the gate calls it
    // that way — it scans, which is what this always did.
    const where = plan?.rows[ch];
    const N = where ? where.length : arr.length;
    for (let w = 0; w < N; w++) {
      const i = where ? where[w] : w;
      const row = arr[i];
      if (!where && !owners.has(row[oi] as string)) continue;
      touched = true;
      const r = row.slice();
      if (ch === "polys" || ch === "fills") {
        const pts = (r[2] as number[]).slice();
        for (let i = 0; i + 1 < pts.length; i += 2) {
          const x = pts[i], y = pts[i + 1];
          pts[i] = fx(x, y); pts[i + 1] = fy(x, y);
        }
        r[2] = pts;
      } else {                                          // texts, marks, rays: x, y at 2, 3
        const x = r[2] as number, y = r[3] as number;
        r[2] = fx(x, y); r[3] = fy(x, y);
      }
      next[i] = r;
    }
    // ⚠ Only replace the channel if something in it moved. `gpu/build.ts` caches a fill's
    // triangles in a `WeakMap` keyed on the point array's IDENTITY, and handing back a fresh
    // array of the same rows would throw that cache away on every edit — 316 ms of it.
    if (touched) out[ch] = next;
  }
  note("__pdcadLocalMs", performance.now() - _t0);
  return out as unknown as T;
}

/** ⚠⚠⚠ **THE KINDS A LOCAL TRANSLATION REPRODUCES EXACTLY, AND NOTHING ELSE MAY BE GUESSED.**
 *  Not a judgement — the verdict of `qa/optimistic_check.mjs`, which moves each kind on its own
 *  through the RUNNING service and compares `applyLocalTransform` against the delta the server
 *  actually sent, number by number, on drawings 1158, 302 and 50:
 *
 *      LINE LWPOLYLINE POLYLINE CIRCLE ARC ELLIPSE SPLINE DIMENSION   exact (0, worst 1.5e-11)
 *      INSERT     0 exact, 2 differ — worst 2.8e+2
 *      LEADER     2 exact, 1 differ — it decomposes through its arrowhead BLOCK
 *      HATCH      the row COUNT changes: the pattern regenerates on a grid, so moving a hatch
 *                 does not translate its lines, it makes different ones
 *
 *  ⚠ A block reference and a hatch are the two most position-dependent things in the format, and
 *  they are also common — so this is deliberately a SMALL win taken safely rather than a large
 *  one taken on trust. Re-run the gate before adding a kind; do not reason one in.
 */
//: ⚠⚠⚠ **DIMENSION WAS IN HERE AND IS NOT EXACT — the gate says so at 3.8e+3.** A dimension is
//: not geometry that moves, it is a MEASUREMENT that redraws: the server regenerates its text,
//: its arrows and its extension lines from the new position, and the browser can only translate
//: the ones it already had. Two of three subjects agreed and the third did not, which is what a
//: kind looks like when it happens to be axis-aligned. Drawing it optimistically meant a drafter
//: moving a dimension saw it land and then JUMP when the server answered.
//: ⚠ Found by `qa/optimistic_check.mjs --op=move`, which reports every SHIPPED kind it cannot
//: confirm. It was shipped before this session; the gate's tolerance was too strict to be
//: believed and the list was never reconciled with it.
//: ⚠⚠ **AND SPLINE IS FLAKY, WHICH IS THE SAME ANSWER AS WRONG.** It passed two runs of the
//: gate and failed the next, on the same drawings — the server re-fits the curve from its
//: control points and the browser can only move the vertices it was given, so whether the two
//: agree depends on the spline. A kind that is exact four times in five is a spline that jumps
//: once in five, and a drafter cannot tell which one they are about to get.
export const LOCALLY_EXACT_KINDS: ReadonlySet<string> = new Set(
  ["LINE", "LWPOLYLINE", "POLYLINE", "CIRCLE", "ARC", "ELLIPSE"]);

/** May these owners be drawn moved before the server confirms it?
 *
 * ⚠⚠ **THE KIND IS READ OFF THE DISPLAY LIST, NOT OFF THE SELECTION.** `Studio`'s `sel` carries a
 * `kind`, and in at least one path it is a PLACEHOLDER — the result of a JOIN is pushed back as
 * `"LWPOLYLINE"` whatever it is. Guessing a block reference's geometry because a stale label said
 * LWPOLYLINE would draw the wrong picture, so the row's own `ownerKind` is what counts.
 * ⚠ A handle the list does not draw at all is refused: absence is not evidence of a safe kind.
 */
/** The kinds each op reproduces exactly, per `qa/optimistic_check.mjs --op=<op>`.
 *
 * ⚠⚠⚠ **PER OP, BECAUSE A KIND THAT IS EXACT UNDER MOVE NEED NOT BE UNDER ROTATE.** A
 * translation moves points; a rotation and a scale re-derive them, and the server re-decomposes
 * the entity from its DXF while the browser transforms points it flattened earlier. The gate has
 * taken `--op=` since it was written and only MOVE was ever wired up, so ROTATE and SCALE waited
 * out the whole round trip for no reason anybody had measured.
 * ⚠ Run the gate before adding to these. Under ROTATE a CIRCLE comes back 3.6e+2 out, an INSERT
 * 1.0e+2, a HATCH 1.4e+4 and a DIMENSION 2.0e+3 — a dimension re-measures itself when it turns,
 * so its text and arrows are not the old ones moved. Under SCALE, LEADER and DIMENSION go too.
 * ⚠ Verified twice each, on drawings 1158, 1152, 302 and 50. */
export const LOCALLY_EXACT_BY_OP: Record<string, ReadonlySet<string>> = {
  move: LOCALLY_EXACT_KINDS,
  rotate: new Set(["LINE", "LWPOLYLINE", "POLYLINE", "ARC", "ELLIPSE", "LEADER"]),
  scale: new Set(["LINE", "LWPOLYLINE", "POLYLINE"]),
};

export function canTransformLocally<T>(dl: T, handles: string[],
                                       op: string = "move"): boolean {
  if (!handles.length) return false;
  const allowed = LOCALLY_EXACT_BY_OP[op];
  if (!allowed) return false;
  const src = dl as unknown as Record<string, unknown>;
  // ⚠⚠⚠ **ONLY THE SELECTED OWNERS, AND IT STOPS WHEN IT HAS THEM ALL.** This built a
  // `Map` of EVERY owner's kind — 1.5 million rows on `ysl-hn-center-2025` — to answer a
  // question about the handful a drafter has selected, and it ran inside the same render as
  // `applyLocalTransform`, which is the other full pass. Together they were the 56.5 ms
  // optimistic render. ⚠ A selection is usually recent geometry and recent geometry sorts late,
  // so the early exit rarely gets to leave early on the FIRST channel — but it does leave the
  // other four, and it stops allocating a map of eleven thousand owners nobody asked about.
  const want = new Set(handles.map((h) => h.toUpperCase()));
  const kindOf = new Map<string, string>();
  for (const [ch, oi, ki] of [["polys", 3, 4], ["texts", 7, 8], ["fills", 3, 4],
                              ["marks", 4, 5], ["rays", 7, 8]] as const) {
    if (kindOf.size >= want.size) break;
    const rows = (src[ch] as unknown[][] | undefined) ?? [];
    for (let i = 0; i < rows.length; i++) {
      const o = rows[i][oi] as string;
      if (!o || !want.has(o) || kindOf.has(o)) continue;
      kindOf.set(o, rows[i][ki] as string);
      if (kindOf.size >= want.size) break;
    }
  }
  return handles.every((h) => {
    const k = kindOf.get(h.toUpperCase());
    return !!k && allowed.has(k);
  });
}
